import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@environments/environment';
import { AppConfigService } from '@eucoms/common-components';
import { CustomerTouchPointsTracker, TrackerConfig, TrackData, CustomerInfo, OpCo as CustTpOpCo, InteractionChannelObject, UserAuthConfig } from '@eucoms/customer-touchpoints-tracker';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';
import { Observable, from } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CustomerTouchPointsService {
  private trackerInstance: CustomerTouchPointsTracker | null = null;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object,
      private authService: AuthService,
      private appConfigService: AppConfigService) {
    this.isBrowser = isPlatformBrowser(platformId);
    // Initialize only in the browser environment where DOM APIs (like sendBeacon) exist
    if (this.isBrowser) {
        this.initializeTracker();
    } else {
        console.warn("TouchPoint Tracker not initialized (not running in browser environment).");
    }
  }

  private initializeTracker(): void {
    const currentOpCo = this.appConfigService.config.opCo;
    let apiBaseUrl = this.appConfigService.config.custtpApiUrl;

    if (!apiBaseUrl || apiBaseUrl === 'https://not-set') {
        console.error("Touchpoint Tracker: API Base URL is not configured for OpCo:", currentOpCo);
        return; // Do not initialize if URL is missing
    }

    // If apiBaseUrl is relative, prepend window.location.origin
    if (apiBaseUrl && !/^https?:\/\//i.test(apiBaseUrl)) {
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        apiBaseUrl = window.location.origin + (apiBaseUrl.startsWith('/') ? '' : '/') + apiBaseUrl;
      }
    }

    const config: TrackerConfig = {
      apiBaseUrl: apiBaseUrl,
      opCo: currentOpCo as CustTpOpCo,
      channel: InteractionChannelObject.Web, // Use 'web' channel for Angular
      batchSize: 5,
      auth: {
        authMode: 'user',
        getToken: () => this.authService.getAccessTokenAsPromise()
      },
      flushInterval: 10000, // flush every 10 seconds
      debug: !environment.production, // Enable debug logs in non-prod
      autoStartSession: false, // Automatically initialize the tracker
    };

    try {
        this.trackerInstance = new CustomerTouchPointsTracker(config);
        console.log("Touchpoint Tracker Initialized for OpCo:", currentOpCo);
    } catch (error) {
        console.error("Failed to initialize Touchpoint Tracker:", error);
        // should we disabled tracking if initialization fails?
    }
  }

  // Wrapper Methods (Provides a clean Angular API) ---
  private ensureTrackerInitialized(): void {
    if (!this.trackerInstance) {
      console.error("Touchpoint Tracker not initialized. Cannot track events.");
      // throw new Error("Touchpoint Tracker not initialized."); <-- consider throwing an error
      return;
    }
  }

  public startSession(): Observable<void> {
    this.ensureTrackerInitialized();
    if (this.trackerInstance) {
        return from(this.trackerInstance.startSession());
    } else {
        console.error("Touchpoint Tracker instance is not available.");
    }
  }

  /**
   * Tracks a specific touchpoint event.
   */
  public track(data: TrackData): Observable<void> {
    this.ensureTrackerInitialized();
    return from(this.trackerInstance.track(data));
  }

  /**
   * Tracks a standard navigation event.
   */
  public trackNavigation(target: string, context?: Partial<TrackData>): void {
    this.ensureTrackerInitialized();
    // Ensure customerInfo exists if context is provided but doesn't have it
    const contextToSend = context ?? {};
    if (!contextToSend.customerInfo) {
        contextToSend.customerInfo = {};
    }
    this.trackerInstance.trackNavigation(target, contextToSend);
  }

  /**
   * Updates the tracker with identified user information (account, customer details).
   */
  public identify(context: { accountNumber: string; customerInfo?: Partial<CustomerInfo> }): void {
    this.ensureTrackerInitialized();
    this.trackerInstance.identify(context);
  }

  /**
   * Manually triggers sending any buffered touchpoints.
   */
  public flush(): void {
    this.ensureTrackerInitialized();
    this.trackerInstance.flush();
  }

  /**
   * Explicitly ends the tracking session (e.g., on user logout).
   */
  public endSession(): Observable<void> {
     if (!this.trackerInstance) {
        return;
    }
    return from(this.trackerInstance.endSession());
  }

  ngOnDestroy(): void {
    if (this.isBrowser && this.trackerInstance) {
        this.endSession();
    }
  }
}
